import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'recompose';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import ComingSoonModal from 'components/ComingSoonModal';
import EducationDrawer from 'components/EducationDrawer';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import QuickActions from 'components/QuickActions';
import SeasonPassIcon from 'components/SeasonPassIcon';
import Toaster from 'components/Toaster';
// Import the SAME styled components from CustomerDetail
// Import the same AnimatedMetric component
import AnimatedMetric from 'pages/track/Customers/components/Customers/AnimatedMetric';
import OpenCreditsCard from 'pages/track/Customers/components/OpenCreditsCard';
import CustomerDetailSkeleton from 'pages/track/Customers/CustomerDetail/CustomerDetailSkeleton';
import {
  Container,
  CustomerHeaderContainer,
  CustomerHeaderLeft,
  CustomerAvatarWrapper,
  CustomerAvatar,
  CustomerInfo,
  CustomerName,
  CustomerMeta,
  CustomerMetaItem,
  CustomerHeaderRight,
  LifetimeValue,
  LifetimeAmount,
  LifetimeLabel,
  DashboardLayout,
  MainContent,
  Sidebar,
  MetricsGrid,
  PatternsCard,
  PatternsTitle,
  FilterPills,
  PatternHighlight,
  PatternLabel,
  PatternValue,
  PatternSection,
  PatternSectionTitle,
  LoadingContainer,
  DrawerSection,
  HelpButton,
  PatternsTitleContainer,
  IconWrapper,
  HabitGrid,
  HabitItem,
  HabitIcon,
  HabitContent,
  HabitTitle,
  HabitDescription,
  CountBadge,
} from 'pages/track/Customers/CustomerDetail/StyledComponents';
import { formatTimestamp } from 'shared/formatters';
// Import the new user-specific HOCs
import TransactionListSection from './components/TransactionListSection';
import GetUserBehavioralStatsAdmin from './gql/queries/get-user-behavioral-stats-admin';
import GetUserDetailStatsAdmin from './gql/queries/get-user-detail-stats-admin';
import GetUserThirdPartyPurchasesAdmin from './gql/queries/get-user-third-party-purchases-admin';
import GetUserTransactionsAdmin from './gql/queries/get-user-transactions-admin';

// Helper function to get ticket/pass text based on platform
const getTicketText = (count = 1) => {
  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
  if (isTicketHoss) {
    return count === 1 ? 'ticket' : 'tickets';
  }
  return count === 1 ? 'pass' : 'passes';
};

const UserTransactions = ({
  data,
  thirdPartyData,
  statsData,
  behavioralData,
  loading,
  error,
  match,
}) => {
  const history = useHistory();
  const location = useLocation();
  const userId = match?.params?.id;

  // Parse page and year from URL
  const searchParams = new URLSearchParams(location.search);
  const currentPageFromUrl = parseInt(searchParams.get('page') || '1');
  const yearFromUrl = searchParams.get('year') || 'All';
  const thirdPartyPageFromUrl = parseInt(
    searchParams.get('thirdPartyPage') || '1'
  );
  const thirdPartyYearFromUrl = searchParams.get('thirdPartyYear') || 'All';

  const [activeTab, setActiveTab] = useState(yearFromUrl);
  const [expandedItems, setExpandedItems] = useState({});
  const [currentPage, setCurrentPage] = useState(currentPageFromUrl);
  const [toasterData, setToasterData] = useState(null);
  const [showBehavioralEducation, setShowBehavioralEducation] = useState(false);

  // State for third-party purchases section
  const [thirdPartyActiveTab, setThirdPartyActiveTab] = useState(
    thirdPartyYearFromUrl
  );
  const [thirdPartyExpandedItems, setThirdPartyExpandedItems] = useState({});
  const [thirdPartyCurrentPage, setThirdPartyCurrentPage] = useState(
    thirdPartyPageFromUrl
  );

  // Sync activeTab with URL when location changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    const newYear = newSearchParams.get('year') || 'All';
    const newPage = parseInt(newSearchParams.get('page') || '1');
    setActiveTab(newYear);
    setCurrentPage(newPage);
  }, [location.search]);

  // Check for success message from Issue Credit on mount
  useEffect(() => {
    const creditSuccessMessage = sessionStorage.getItem('creditSuccessMessage');
    if (creditSuccessMessage) {
      setToasterData({
        title: 'Credit Issued!',
        message: creditSuccessMessage,
        type: 'success',
      });
      // Clear the message from sessionStorage
      sessionStorage.removeItem('creditSuccessMessage');
    }
  }, []);

  // Determine the route prefix
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Check if user is Admin
  const isAdmin = location.pathname.includes('/admin/');

  // Get user data from API response
  const transactionData = data?.getUserTransactionsAdmin?.results || [];
  const totalCount = data?.getUserTransactionsAdmin?.count || 0;
  const customer = data?.getUserTransactionsAdmin?.customer || {};
  const itemsPerPage = 15;

  // Calculate total pages based on count from API
  let totalPages;
  if (totalCount === 0 || transactionData.length === 0) {
    totalPages = currentPage > 1 ? currentPage - 1 : 0;
  } else if (transactionData.length < itemsPerPage) {
    totalPages = currentPage;
  } else {
    const calculatedPages = Math.ceil(totalCount / itemsPerPage);

    if (
      currentPage === 1 &&
      transactionData.length === itemsPerPage &&
      totalCount > itemsPerPage &&
      totalCount <= itemsPerPage + 1
    ) {
      totalPages = 1;
    } else {
      totalPages = calculatedPages;
    }
  }

  // Loading state
  if (
    loading ||
    statsData?.loading ||
    behavioralData?.loading ||
    !customer ||
    !customer.id
  ) {
    return <CustomerDetailSkeleton />;
  }

  // Error state
  if (error || statsData?.error || behavioralData?.error) {
    return (
      <EnhancedThemeProvider>
        <LoadingContainer>
          <div>
            Error loading user details:{' '}
            {error?.message || statsData?.error?.message}
          </div>
        </LoadingContainer>
      </EnhancedThemeProvider>
    );
  }

  const hasTransactions = transactionData && transactionData.length > 0;

  // Handle page change
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setExpandedItems({});

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', page.toString());
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // Helper function to parse currency strings
  const parseCurrency = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    return parseFloat(value.replace(/[$,]/g, '')) || 0;
  };

  // Format phone number to (323) 842-1428 format
  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[2]}) ${match[3]}-${match[4]}`;
    }
    return phone;
  };

  // Get user info
  const customerName =
    `${customer.first_name || ''} ${customer.last_name || ''}`.trim() ||
    'Unknown User';
  const customerEmail = customer.email || 'No email';
  const customerPhone = formatPhone(customer.cellphone) || 'No phone';
  const customerSince = customer.customer_since || null;

  // Calculate metrics
  const totalOrders =
    statsData?.getUserDetailStatsAdmin?.totalOrders?.value || totalCount || 0;
  const pageRevenue = hasTransactions
    ? transactionData.reduce((sum, t) => sum + parseCurrency(t.total), 0)
    : 0;
  const avgPageOrderValue =
    hasTransactions && transactionData.length > 0
      ? pageRevenue / transactionData.length
      : 0;
  const totalRevenue = avgPageOrderValue * totalOrders;
  const avgOrderValue =
    totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(2) : 0;

  // Format transactions for display
  const formattedTransactions = hasTransactions
    ? transactionData.map((t) => {
        const purchaseDate = new Date(Number(t.purchase_date));
        // Collect all tickets from all users (for Pit Pay)
        const tickets = t.users
          ? t.users.flatMap((user) => user.tickets || [])
          : [];
        const ticketSummary =
          tickets
            .map((ticket) => ticket.name)
            .filter(Boolean)
            .join(', ') || `No ${getTicketText(2)} details`;

        const isNonEventPurchase = !t.event;
        let eventName,
          trackName,
          displayMonth,
          displayDay,
          isMultiDay = false;

        if (isNonEventPurchase) {
          const ticketTypes = tickets
            .map((ticket) => ticket.type)
            .filter(Boolean);
          const hasAnnual = ticketTypes.some((type) => type === 'annual');
          const hasMembership = ticketTypes.some(
            (type) => type === 'membership'
          );

          if (hasAnnual && !hasMembership) {
            eventName = 'Annual Pass Purchase';
          } else if (hasMembership && !hasAnnual) {
            eventName = 'Membership Purchase';
          } else if (hasAnnual && hasMembership) {
            eventName = 'Pass/Membership Purchase';
          } else {
            eventName = 'Pass Purchase';
          }

          trackName = '';
          displayMonth = '';
          displayDay = purchaseDate.getFullYear().toString();
        } else {
          eventName = t.event.name || 'Unknown Event';
          trackName = t.track?.name || 'Unknown Track';

          if (t.event.start_date && t.event.end_date) {
            let startMs, endMs;

            if (
              typeof t.event.start_date === 'string' &&
              (t.event.start_date.includes('-') ||
                t.event.start_date.includes('T'))
            ) {
              startMs = new Date(t.event.start_date).getTime();
            } else {
              const startTimestamp =
                typeof t.event.start_date === 'string'
                  ? parseInt(t.event.start_date)
                  : t.event.start_date;
              startMs =
                startTimestamp.toString().length <= 10
                  ? startTimestamp * 1000
                  : startTimestamp;
            }

            if (
              typeof t.event.end_date === 'string' &&
              (t.event.end_date.includes('-') || t.event.end_date.includes('T'))
            ) {
              endMs = new Date(t.event.end_date).getTime();
            } else {
              const endTimestamp =
                typeof t.event.end_date === 'string'
                  ? parseInt(t.event.end_date)
                  : t.event.end_date;
              endMs =
                endTimestamp.toString().length <= 10
                  ? endTimestamp * 1000
                  : endTimestamp;
            }

            isMultiDay = startMs !== endMs;

            if (isMultiDay) {
              const startMonth = formatTimestamp(startMs, 'MMM', true);
              const startDay = formatTimestamp(startMs, 'DD', true);
              const endMonth = formatTimestamp(endMs, 'MMM', true);
              const endDay = formatTimestamp(endMs, 'DD', true);

              if (startMonth === endMonth) {
                displayMonth = startMonth;
                displayDay = `${startDay}-${endDay}`;
              } else {
                displayMonth = `${startMonth}-${endMonth}`;
                displayDay = `${startDay}-${endDay}`;
              }
            } else {
              displayMonth = formatTimestamp(startMs, 'MMM', true);
              displayDay = formatTimestamp(startMs, 'DD', true);
            }
          } else if (t.event.start_date) {
            let startMs;
            if (
              typeof t.event.start_date === 'string' &&
              (t.event.start_date.includes('-') ||
                t.event.start_date.includes('T'))
            ) {
              startMs = new Date(t.event.start_date).getTime();
            } else {
              const startTimestamp =
                typeof t.event.start_date === 'string'
                  ? parseInt(t.event.start_date)
                  : t.event.start_date;
              startMs =
                startTimestamp.toString().length <= 10
                  ? startTimestamp * 1000
                  : startTimestamp;
            }
            displayMonth = formatTimestamp(startMs, 'MMM', true);
            displayDay = formatTimestamp(startMs, 'DD', true);
          } else {
            displayMonth = formatTimestamp(
              Number(t.purchase_date),
              'MMM',
              true
            );
            displayDay = formatTimestamp(Number(t.purchase_date), 'DD', true);
          }
        }

        return {
          id: t.id,
          year: purchaseDate.getFullYear().toString(),
          month: displayMonth,
          day: displayDay,
          eventName: eventName,
          trackName: trackName,
          ticketInfo: ticketSummary,
          ticketCount: tickets.length,
          total: parseCurrency(t.total),
          profit: parseCurrency(t.profit),
          fee: parseCurrency(t.fee),
          discount: parseCurrency(t.discount),
          refund: parseCurrency(t.refund),
          status: t.status || 'completed',
          orderSource: t.order_source || 'App',
          tickets: tickets,
          isNonEventPurchase: isNonEventPurchase,
          isMultiDay: isMultiDay,
          purchase_date: Number(t.purchase_date),
          isThirdParty: false,
          eventStatus: t.event?.status || null,
        };
      })
    : [];

  // Get yearCounts from API
  const yearCountsData = data?.getUserTransactionsAdmin?.yearCounts || [];

  const toggleExpand = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Third-party purchases data and logic
  const thirdPartyTransactionData =
    thirdPartyData?.getUserThirdPartyPurchasesAdmin?.results || [];
  const thirdPartyTotalCount =
    thirdPartyData?.getUserThirdPartyPurchasesAdmin?.count || 0;
  const hasThirdPartyPurchases =
    thirdPartyTransactionData && thirdPartyTransactionData.length > 0;
  const thirdPartyYearCountsData =
    thirdPartyData?.getUserThirdPartyPurchasesAdmin?.yearCounts || [];

  // Calculate third-party total pages
  let thirdPartyTotalPages;
  if (thirdPartyTotalCount === 0 || thirdPartyTransactionData.length === 0) {
    thirdPartyTotalPages =
      thirdPartyCurrentPage > 1 ? thirdPartyCurrentPage - 1 : 0;
  } else if (thirdPartyTransactionData.length < itemsPerPage) {
    thirdPartyTotalPages = thirdPartyCurrentPage;
  } else {
    const calculatedPages = Math.ceil(thirdPartyTotalCount / itemsPerPage);
    thirdPartyTotalPages = calculatedPages;
  }

  // Format third-party transactions for display
  const formattedThirdPartyTransactions = hasThirdPartyPurchases
    ? thirdPartyTransactionData.map((t) => {
        const purchaseDate = new Date(Number(t.purchase_date));
        // Collect all tickets from all users (for Pit Pay)
        const tickets = t.users
          ? t.users.flatMap((user) => user.tickets || [])
          : [];
        const ticketSummary =
          tickets
            .map((ticket) => ticket.name)
            .filter(Boolean)
            .join(', ') || `No ${getTicketText(2)} details`;

        const isNonEventPurchase = !t.event;
        let eventName,
          trackName,
          displayMonth,
          displayDay,
          isMultiDay = false;

        if (isNonEventPurchase) {
          const ticketTypes = tickets
            .map((ticket) => ticket.type)
            .filter(Boolean);
          const hasAnnual = ticketTypes.some((type) => type === 'annual');
          const hasMembership = ticketTypes.some(
            (type) => type === 'membership'
          );

          if (hasAnnual && !hasMembership) {
            eventName = 'Annual Pass Purchase';
          } else if (hasMembership && !hasAnnual) {
            eventName = 'Membership Purchase';
          } else {
            eventName = 'Multi-Product Purchase';
          }
          trackName = t.track?.name || 'Unknown';
          displayMonth = purchaseDate.toLocaleDateString('en-US', {
            month: 'short',
          });
          displayDay = purchaseDate.getDate();
        } else {
          eventName = t.event.name || 'Unknown Event';
          trackName = t.track?.name || 'Unknown Track';

          if (t.event.start_date && t.event.end_date) {
            let startMs, endMs;

            if (
              typeof t.event.start_date === 'string' &&
              (t.event.start_date.includes('-') ||
                t.event.start_date.includes('T'))
            ) {
              startMs = new Date(t.event.start_date).getTime();
            } else {
              const startTimestamp =
                typeof t.event.start_date === 'string'
                  ? parseInt(t.event.start_date)
                  : t.event.start_date;
              startMs =
                startTimestamp.toString().length <= 10
                  ? startTimestamp * 1000
                  : startTimestamp;
            }

            if (
              typeof t.event.end_date === 'string' &&
              (t.event.end_date.includes('-') || t.event.end_date.includes('T'))
            ) {
              endMs = new Date(t.event.end_date).getTime();
            } else {
              const endTimestamp =
                typeof t.event.end_date === 'string'
                  ? parseInt(t.event.end_date)
                  : t.event.end_date;
              endMs =
                endTimestamp.toString().length <= 10
                  ? endTimestamp * 1000
                  : endTimestamp;
            }

            const startDate = new Date(startMs);
            const endDate = new Date(endMs);
            isMultiDay = startDate.getTime() !== endDate.getTime();

            if (isMultiDay) {
              displayMonth = `${startDate.toLocaleDateString('en-US', {
                month: 'short',
              })} ${startDate.getDate()}-${endDate.getDate()}`;
              displayDay = '';
            } else {
              displayMonth = startDate.toLocaleDateString('en-US', {
                month: 'short',
              });
              displayDay = startDate.getDate();
            }
          } else if (t.event.start_date) {
            let startMs;
            if (
              typeof t.event.start_date === 'string' &&
              (t.event.start_date.includes('-') ||
                t.event.start_date.includes('T'))
            ) {
              startMs = new Date(t.event.start_date).getTime();
            } else {
              const startTimestamp =
                typeof t.event.start_date === 'string'
                  ? parseInt(t.event.start_date)
                  : t.event.start_date;
              startMs =
                startTimestamp.toString().length <= 10
                  ? startTimestamp * 1000
                  : startTimestamp;
            }
            displayMonth = formatTimestamp(startMs, 'MMM', true);
            displayDay = formatTimestamp(startMs, 'DD', true);
          } else {
            displayMonth = formatTimestamp(
              Number(t.purchase_date),
              'MMM',
              true
            );
            displayDay = formatTimestamp(Number(t.purchase_date), 'DD', true);
          }
        }

        return {
          id: t.id,
          year: purchaseDate.getFullYear().toString(),
          month: displayMonth,
          day: displayDay,
          eventName: eventName,
          trackName: trackName,
          ticketInfo: ticketSummary,
          ticketCount: tickets.length,
          total: parseCurrency(t.total),
          profit: parseCurrency(t.profit),
          fee: parseCurrency(t.fee),
          discount: parseCurrency(t.discount),
          refund: parseCurrency(t.refund),
          status: t.status || 'completed',
          orderSource: t.order_source || 'App',
          tickets: tickets,
          isNonEventPurchase: isNonEventPurchase,
          isMultiDay: isMultiDay,
          purchaser: t.user
            ? `${t.user.first_name || ''} ${t.user.last_name || ''}`.trim()
            : 'Unknown',
          purchaserEmail: t.user?.email || '',
          purchase_date: Number(t.purchase_date),
          isThirdParty: true,
          eventStatus: t.event?.status || null,
        };
      })
    : [];

  const filteredTransactions = formattedTransactions;

  // Handle third-party page change
  const handleThirdPartyPageChange = (page) => {
    if (page < 1 || page > thirdPartyTotalPages) return;
    setThirdPartyCurrentPage(page);
    setThirdPartyExpandedItems({});

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('thirdPartyPage', page.toString());
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  // Generate page numbers for third-party pagination
  const getThirdPartyPageNumbers = () => {
    const pages = [];
    if (thirdPartyTotalPages <= 7) {
      for (let i = 1; i <= thirdPartyTotalPages; i++) {
        pages.push(i);
      }
    } else {
      if (thirdPartyCurrentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(thirdPartyTotalPages);
      } else if (thirdPartyCurrentPage >= thirdPartyTotalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = thirdPartyTotalPages - 3; i <= thirdPartyTotalPages; i++)
          pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (
          let i = thirdPartyCurrentPage - 1;
          i <= thirdPartyCurrentPage + 1;
          i++
        )
          pages.push(i);
        pages.push('...');
        pages.push(thirdPartyTotalPages);
      }
    }
    return pages;
  };

  const toggleThirdPartyExpand = (itemId) => {
    setThirdPartyExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const fromTransaction = location.state?.from;

  return (
    <EnhancedThemeProvider>
      <Container>
        {fromTransaction && (
          <ButtonV2
            onClick={() => history.push(fromTransaction)}
            style={{ marginBottom: '16px', alignSelf: 'flex-start' }}
          >
            ← Back to Transaction
          </ButtonV2>
        )}
        {/* User Header */}
        <CustomerHeaderContainer>
          <CustomerHeaderLeft>
            <CustomerAvatarWrapper>
              <CustomerAvatar>
                {customer.first_name && customer.last_name
                  ? `${customer.first_name[0]}${customer.last_name[0]}`.toUpperCase()
                  : customerName
                      .split(' ')
                      .slice(0, 2)
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()}
              </CustomerAvatar>
              {customer.has_season_pass && <SeasonPassIcon />}
            </CustomerAvatarWrapper>
            <CustomerInfo>
              <CustomerName>{customerName}</CustomerName>
              <CustomerMeta>
                {customerPhone && (
                  <CustomerMetaItem>
                    <a href={`tel:${customer.cellphone}`}>{customerPhone}</a>
                  </CustomerMetaItem>
                )}
                <CustomerMetaItem>
                  <a href={`mailto:${customerEmail}`}>{customerEmail}</a>
                </CustomerMetaItem>
              </CustomerMeta>
            </CustomerInfo>
          </CustomerHeaderLeft>
          <CustomerHeaderRight>
            <div>
              <LifetimeValue>
                <LifetimeAmount>
                  {data?.getUserTransactionsAdmin?.customer?.lifetime_value ||
                    '$0.00'}
                </LifetimeAmount>
                <LifetimeLabel>Lifetime Value</LifetimeLabel>
              </LifetimeValue>
              {customerSince && (
                <div
                  style={{
                    marginTop: '12px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '14px',
                  }}
                >
                  Customer since {customerSince}
                </div>
              )}
            </div>
          </CustomerHeaderRight>
        </CustomerHeaderContainer>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Metrics Grid */}
            <MetricsGrid>
              <AnimatedMetric
                value={
                  statsData?.getUserDetailStatsAdmin?.avgOrderValue?.value ||
                  parseFloat(avgOrderValue)
                }
                label="Avg Order Value"
                isCurrency={true}
                changeText={
                  statsData?.getUserDetailStatsAdmin?.avgOrderValue
                    ?.changeText || '+0%'
                }
                isPositive={
                  statsData?.getUserDetailStatsAdmin?.avgOrderValue
                    ?.isPositive || false
                }
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5 8 5.961 14.154 3.5zM15 4.239l-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923zM7.443.184a1.5 1.5 0 0 1 1.114 0l7.129 2.852A.5.5 0 0 1 16 3.5v8.662a1 1 0 0 1-.629.928l-7.185 2.874a.5.5 0 0 1-.372 0L.63 13.09a1 1 0 0 1-.63-.928V3.5a.5.5 0 0 1 .314-.464z" />
                  </svg>
                }
              />
              <AnimatedMetric
                value={statsData?.getUserDetailStatsAdmin?.spentYTD?.value || 0}
                label="Spent YTD"
                changeText={
                  statsData?.getUserDetailStatsAdmin?.spentYTD?.changeText ||
                  '$0'
                }
                isPositive={
                  statsData?.getUserDetailStatsAdmin?.spentYTD?.isPositive ||
                  false
                }
                isCurrency={true}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M1 3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1zm7 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4" />
                    <path d="M0 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1zm3 0a2 2 0 0 1-2 2v4a2 2 0 0 1 2 2h10a2 2 0 0 1 2-2V7a2 2 0 0 1-2-2z" />
                  </svg>
                }
              />
              <AnimatedMetric
                value={
                  statsData?.getUserDetailStatsAdmin?.totalOrders?.value ||
                  totalOrders
                }
                label="Total Orders"
                changeText={
                  statsData?.getUserDetailStatsAdmin?.totalOrders?.changeText ||
                  '+0'
                }
                isPositive={
                  statsData?.getUserDetailStatsAdmin?.totalOrders?.isPositive ||
                  false
                }
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M0 2.5A.5.5 0 0 1 .5 2H2a.5.5 0 0 1 .485.379L2.89 4H14.5a.5.5 0 0 1 .485.621l-1.5 6A.5.5 0 0 1 13 11H4a.5.5 0 0 1-.485-.379L1.61 3H.5a.5.5 0 0 1-.5-.5M3.14 5l1.25 5h8.22l1.25-5zM5 13a1 1 0 1 0 0 2 1 1 0 0 0 0-2m-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0m9-1a1 1 0 1 0 0 2 1 1 0 0 0 0-2m-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0" />
                  </svg>
                }
              />
              <AnimatedMetric
                value={
                  statsData?.getUserDetailStatsAdmin?.eventsAttendedYTD
                    ?.value || 0
                }
                label="Events Attended YTD"
                isPercentage={false}
                changeText={
                  statsData?.getUserDetailStatsAdmin?.eventsAttendedYTD
                    ?.changeText || ''
                }
                isPositive={
                  statsData?.getUserDetailStatsAdmin?.eventsAttendedYTD
                    ?.isPositive || false
                }
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                  </svg>
                }
              />
            </MetricsGrid>

            {/* Transaction History */}
            <TransactionListSection
              title="Transaction History"
              transactions={filteredTransactions}
              transactionData={transactionData}
              hasTransactions={hasTransactions}
              expandedItems={expandedItems}
              toggleExpand={toggleExpand}
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              handlePageChange={handlePageChange}
              getPageNumbers={getPageNumbers}
              yearCountsData={yearCountsData}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              setCurrentPage={setCurrentPage}
              showPurchaser={false}
              routePrefix={routePrefix}
              userId={userId}
              isAdmin={isAdmin}
            />

            {/* Third-Party Purchases Section */}
            {hasThirdPartyPurchases && (
              <TransactionListSection
                title="Purchases Made for This User"
                transactions={formattedThirdPartyTransactions}
                transactionData={thirdPartyTransactionData}
                hasTransactions={hasThirdPartyPurchases}
                expandedItems={thirdPartyExpandedItems}
                toggleExpand={toggleThirdPartyExpand}
                currentPage={thirdPartyCurrentPage}
                totalPages={thirdPartyTotalPages}
                totalCount={thirdPartyTotalCount}
                handlePageChange={handleThirdPartyPageChange}
                getPageNumbers={getThirdPartyPageNumbers}
                yearCountsData={thirdPartyYearCountsData}
                activeTab={thirdPartyActiveTab}
                setActiveTab={setThirdPartyActiveTab}
                setCurrentPage={setThirdPartyCurrentPage}
                showPurchaser={true}
                routePrefix={routePrefix}
                userId={userId}
                isAdmin={isAdmin}
                style={{ marginTop: '24px' }}
              />
            )}
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <div style={{ marginBottom: '30px' }}>
              <QuickActions
                actions={[
                  {
                    label: 'Issue Credit',
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                      </svg>
                    ),
                    onClick: () => {
                      const returnPath = `${routePrefix}/users/transactions/${userId}`;
                      history.push(
                        `${routePrefix}/users/issue-credit/${userId}?returnTo=${encodeURIComponent(
                          returnPath
                        )}`
                      );
                    },
                  },
                  {
                    label: 'Edit User',
                    icon: (
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
                      </svg>
                    ),
                    onClick: () => {
                      history.push(`${routePrefix}/users/edit/${userId}`);
                    },
                  },
                  {
                    component: (
                      <ComingSoonModal
                        title="User Messaging"
                        description="We're building a powerful messaging system to help you connect with your users."
                        features={[
                          'Message an individual user directly',
                          'View history of all past conversations',
                          'Get notifications and inbox for user messages',
                        ]}
                      >
                        <ButtonV2
                          elevated
                          icon={
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
                            </svg>
                          }
                        >
                          Message User
                        </ButtonV2>
                      </ComingSoonModal>
                    ),
                  },
                ]}
              />
            </div>

            {/* Open Credits Card */}
            <OpenCreditsCard userId={userId} routePrefix={routePrefix} />

            {/* Behavioral Habits */}
            <PatternsCard>
              <PatternsTitleContainer>
                <PatternsTitle>Behavioral Habits</PatternsTitle>
                <HelpButton
                  onClick={() => setShowBehavioralEducation(true)}
                  aria-label="Learn about behavioral habits"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.496 6.033h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286a.237.237 0 0 0 .241.247m2.325 6.443c.61 0 1.029-.394 1.029-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94 0 .533.425.927 1.01.927z" />
                  </svg>
                </HelpButton>
              </PatternsTitleContainer>

              {/* Purchase Patterns */}
              {behavioralData?.getUserBehavioralStatsAdmin?.purchasePatterns
                ?.length > 0 && (
                <PatternSection>
                  <PatternSectionTitle>Purchase Patterns</PatternSectionTitle>
                  <FilterPills>
                    {behavioralData.getUserBehavioralStatsAdmin.purchasePatterns.map(
                      (pattern, index) => (
                        <Badge key={index} shape="pill" count={pattern.count}>
                          {pattern.label}
                        </Badge>
                      )
                    )}
                  </FilterPills>
                </PatternSection>
              )}

              {/* Purchase Timing */}
              {behavioralData?.getUserBehavioralStatsAdmin
                ?.purchaseTimingPatterns?.length > 0 && (
                <PatternSection>
                  <PatternSectionTitle>
                    Purchase Timing Patterns
                  </PatternSectionTitle>
                  <FilterPills>
                    {behavioralData.getUserBehavioralStatsAdmin.purchaseTimingPatterns.map(
                      (timing, index) => (
                        <Badge key={index} shape="pill" count={timing.count}>
                          {timing.label.charAt(0).toUpperCase() +
                            timing.label.slice(1)}
                        </Badge>
                      )
                    )}
                  </FilterPills>
                </PatternSection>
              )}

              {/* Event Preferences */}
              {behavioralData?.getUserBehavioralStatsAdmin?.eventPreferences
                ?.length > 0 && (
                <PatternSection>
                  <PatternSectionTitle>Event Preferences</PatternSectionTitle>
                  <FilterPills>
                    {behavioralData.getUserBehavioralStatsAdmin.eventPreferences.map(
                      (event, index) => (
                        <Badge key={index} shape="pill" count={event.count}>
                          {event.label}
                        </Badge>
                      )
                    )}
                  </FilterPills>
                </PatternSection>
              )}

              {/* Division Preferences */}
              {behavioralData?.getUserBehavioralStatsAdmin?.divisionPreferences
                ?.length > 0 && (
                <PatternSection>
                  <PatternSectionTitle>
                    Division Preferences
                  </PatternSectionTitle>
                  <FilterPills>
                    {behavioralData.getUserBehavioralStatsAdmin.divisionPreferences.map(
                      (division, index) => (
                        <Badge key={index} shape="pill" count={division.count}>
                          {division.label}
                        </Badge>
                      )
                    )}
                  </FilterPills>
                </PatternSection>
              )}

              {/* Series Preferences */}
              {behavioralData?.getUserBehavioralStatsAdmin?.seriesPreferences
                ?.length > 0 && (
                <PatternSection>
                  <PatternSectionTitle>Series Preferences</PatternSectionTitle>
                  <FilterPills>
                    {behavioralData.getUserBehavioralStatsAdmin.seriesPreferences.map(
                      (series, index) => (
                        <Badge key={index} shape="pill" count={series.count}>
                          {series.label}
                        </Badge>
                      )
                    )}
                  </FilterPills>
                </PatternSection>
              )}

              {/* Average Group Size */}
              {behavioralData?.getUserBehavioralStatsAdmin?.averageGroupSize &&
              hasTransactions ? (
                <PatternHighlight>
                  <PatternLabel>Average group size</PatternLabel>
                  <PatternValue>
                    {
                      behavioralData.getUserBehavioralStatsAdmin
                        .averageGroupSize
                    }{' '}
                    {getTicketText(
                      behavioralData.getUserBehavioralStatsAdmin
                        .averageGroupSize
                    )}
                  </PatternValue>
                </PatternHighlight>
              ) : (
                <PatternHighlight>
                  <PatternLabel>Average group size</PatternLabel>
                  <PatternValue style={{ color: '#6B7280' }}>—</PatternValue>
                </PatternHighlight>
              )}
            </PatternsCard>
          </Sidebar>
        </DashboardLayout>
      </Container>

      {/* Success Toaster */}
      {toasterData && (
        <Toaster
          isOpen={!!toasterData}
          onClose={() => setToasterData(null)}
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
          duration={6000}
        />
      )}

      {/* Behavioral Education Drawer */}
      <EducationDrawer
        title="Understanding Behavioral Habits"
        isOpen={showBehavioralEducation}
        onClose={() => setShowBehavioralEducation(false)}
        showHelpButton={false}
      >
        <DrawerSection>
          <h3>
            <IconWrapper>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            </IconWrapper>
            Understanding the Numbers
          </h3>
          <p>
            Each behavioral pattern shows a <CountBadge>count</CountBadge>{' '}
            representing how many times this user has exhibited that specific
            behavior. Higher counts indicate stronger preferences.
          </p>
        </DrawerSection>

        <DrawerSection>
          <h3>
            <IconWrapper>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </IconWrapper>
            Purchase Timing Patterns
          </h3>
          <p>When do they buy {getTicketText(2)}?</p>
          <HabitGrid>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Weeks Before</HabitTitle>
                <HabitDescription>
                  Plans ahead and secures {getTicketText(2)} early
                </HabitDescription>
              </HabitContent>
            </HabitItem>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M6 2v6h.01L6 8.01 10 12l-4 4 .01.01H6V22h12v-5.99h-.01L18 16l-4-4 4-3.99-.01-.01H18V2H6z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Days Before</HabitTitle>
                <HabitDescription>
                  Buys {getTicketText(2)} within the week of event
                </HabitDescription>
              </HabitContent>
            </HabitItem>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Same Day</HabitTitle>
                <HabitDescription>
                  Last-minute purchaser, decides day-of
                </HabitDescription>
              </HabitContent>
            </HabitItem>
          </HabitGrid>
        </DrawerSection>

        <DrawerSection>
          <h3>
            <IconWrapper>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M2 9a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V9z" />
                <path d="M13 5L13 19" />
              </svg>
            </IconWrapper>
            Purchase Patterns
          </h3>
          <p>What do they typically buy?</p>
          <HabitGrid>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>
                  Family{' '}
                  {getTicketText(2).charAt(0).toUpperCase() +
                    getTicketText(2).slice(1)}
                </HabitTitle>
                <HabitDescription>
                  Buys family packages and group deals
                </HabitDescription>
              </HabitContent>
            </HabitItem>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M4 18v3h3v-3h10v3h3v-6H4v3zm15-8h3v3h-3v-3zM2 10h3v3H2v-3zm15 3H7V5c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v8z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Reserved Seating</HabitTitle>
                <HabitDescription>
                  Prefers guaranteed assigned seats
                </HabitDescription>
              </HabitContent>
            </HabitItem>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Parking Passes</HabitTitle>
                <HabitDescription>
                  Regularly adds parking to their order
                </HabitDescription>
              </HabitContent>
            </HabitItem>
          </HabitGrid>
        </DrawerSection>

        <DrawerSection>
          <h3>
            <IconWrapper>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z" />
              </svg>
            </IconWrapper>
            Event & Division Preferences
          </h3>
          <p>What types of racing do they enjoy?</p>
          <HabitGrid>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M21.04 11.13l-1.48.1c-.21 0-.4.15-.47.35l-.69 2.19-3.74-8.23c-.11-.24-.36-.4-.63-.4-.28 0-.53.17-.63.42l-2.42 6.21H9c-.41 0-.74.34-.74.74s.33.77.74.77h2.54c.27 0 .51-.17.62-.41l1.34-3.43 3.62 7.96c.11.24.36.39.63.39s.52-.16.63-.41l1.46-4.59.5-.03c.41 0 .73-.34.73-.75s-.32-.78-.73-.78zM3.72 11.13c-.37.03-.67.34-.67.74s.3.7.67.74l.5.03 1.46 4.59c.11.25.36.41.63.41s.52-.15.63-.39l3.62-7.96 1.34 3.43c.11.24.35.41.62.41H15c.41 0 .74-.34.74-.77s-.33-.74-.74-.74h-1.98l-2.42-6.21c-.1-.25-.35-.42-.63-.42-.27 0-.52.16-.63.4l-3.74 8.23-.69-2.19c-.07-.2-.26-.35-.47-.35l-1.48-.1c-.41 0-.73.37-.73.78s.32.75.73.75z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Event Types</HabitTitle>
                <HabitDescription>
                  Weekly races, Monster Trucks, Figure 8, Drag Racing, Special
                  Events
                </HabitDescription>
              </HabitContent>
            </HabitItem>
            <HabitItem>
              <HabitIcon>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M5 7c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V4H5v3zm2.5-1.5L9 4l1.5 1.5L12 4l1.5 1.5L15 4l1.5 1.5L17 7H7V5.5zM19 10H5l1.5 10.28c.09.72.73 1.28 1.47 1.28h8.06c.74 0 1.38-.56 1.47-1.28L19 10zm-7 9l-4-4h2.5v-3h3v3H16l-4 4z" />
                </svg>
              </HabitIcon>
              <HabitContent>
                <HabitTitle>Car Divisions</HabitTitle>
                <HabitDescription>
                  Late Models, Sprint Cars, Street Stocks, Modifieds, Mini
                  Stocks
                </HabitDescription>
              </HabitContent>
            </HabitItem>
          </HabitGrid>
        </DrawerSection>
      </EducationDrawer>
    </EnhancedThemeProvider>
  );
};

// Apply all HOCs using compose
const UserTransactionsWithQueries = compose(
  GetUserTransactionsAdmin,
  GetUserThirdPartyPurchasesAdmin,
  GetUserDetailStatsAdmin,
  GetUserBehavioralStatsAdmin
)(UserTransactions);

export default UserTransactionsWithQueries;
